import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { useToast } from "hooks/useToast";
import {
  useAddTaxYearsMutation,
  useGetTaxYearsQuery,
} from "v2/store/taxSetupApi";
import { useGroupId } from "v2/utils/useGroupId";

type AddPriorYearModalProps = {
  open: boolean;
  season: number;
  entityId: string;
  entityName: string;
  onClose: () => void;
  onSuccess: () => void;
};

const GENERIC_ERROR = "Something went wrong";

export const AddPriorYearModal = ({
  open,
  season,
  entityId,
  entityName,
  onClose,
  onSuccess,
}: AddPriorYearModalProps) => {
  const { alertToast } = useToast();
  const groupId = useGroupId();

  const { data: taxYears } = useGetTaxYearsQuery(
    { groupId },
    { skip: !groupId }
  );

  const [addTaxYears, { isLoading }] = useAddTaxYearsMutation();

  const handleOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) onClose();
  };

  const buildTrackedYears = (): string[] => {
    const newYear = String(season);
    const existing =
      taxYears?.entities.find((entity) => entity.uuid === entityId)
        ?.tracked_years ?? [];

    if (existing.includes(newYear)) return existing;

    return [...existing, newYear];
  };

  const handleConfirm = async (): Promise<void> => {
    try {
      await addTaxYears({
        groupId,
        entities: [{ uuid: entityId, tracked_years: buildTrackedYears() }],
      }).unwrap();
      onSuccess();
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        GENERIC_ERROR;
      alertToast({ message });
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange}>
      <Modal.Content useCustomOverlay size="small">
        <Modal.Header>
          <Modal.Title>Add a prior tax year</Modal.Title>
          <Modal.Close />
        </Modal.Header>

        <Modal.Body>
          <div className="t-flex t-flex-col t-gap-4">
            <span className="t-text-body-sm t-text-text-60">
              We&apos;ll include filings for {entityName} for the season{" "}
              {season}
            </span>

            <span className="t-border-0 t-border-t t-border-solid t-border-neutral-0 t-pt-3 t-text-body-sm t-italic t-text-neutral-40">
              Note: We&apos;ll use your existing filing information to estimate
              the filing requirements for Season {season}. You can review and
              make changes before filing.
            </span>
          </div>
        </Modal.Body>

        <Modal.Footer className="t-flex t-justify-end t-gap-2">
          <Button type="button" customType="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            customType="primary"
            onClick={handleConfirm}
            disabled={isLoading}
            isLoading={isLoading}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
